import type { NormalizedLogRecord } from "@/types/logs";
import type { RouteAnalyticsSummary } from "@/types/route-analytics";
import { DEFAULT_ROUTE_ANALYTICS_PRESET, ROUTE_ANALYTICS_TIME_PRESETS } from "@/types/route-analytics";
import { getRouteGroupingField } from "./route-normalizer";

/**
 * Approximate request duration from stored timestamps when both exist.
 * Used by log response-time filters (Route Details), not Route Analytics rollups.
 */
export function getResponseTimeMs(record: NormalizedLogRecord): number | null {
  if (!record.createdAt || !record.updatedAt) return null;
  const ms = record.updatedAt.getTime() - record.createdAt.getTime();
  return Number.isFinite(ms) && ms >= 0 ? ms : null;
}

export interface RouteSummaryAcc {
  route: string;
  totalRequests: number;
  successCount: number;
  errorCount: number;
  lastActivityAt: Date | null;
}

export function createRouteSummaryAcc(route: string): RouteSummaryAcc {
  return {
    route,
    totalRequests: 0,
    successCount: 0,
    errorCount: 0,
    lastActivityAt: null,
  };
}

export function accumulateRouteRecord(
  acc: RouteSummaryAcc,
  record: NormalizedLogRecord,
): void {
  acc.totalRequests += 1;

  if (record.errorClassification === "success") {
    acc.successCount += 1;
  } else if (
    record.errorClassification === "error" ||
    record.errorClassification === "malformed"
  ) {
    acc.errorCount += 1;
  }

  const activity = record.updatedAt ?? record.createdAt;
  if (activity && (!acc.lastActivityAt || activity.getTime() > acc.lastActivityAt.getTime())) {
    acc.lastActivityAt = activity;
  }
}

export function finalizeRouteSummary(acc: RouteSummaryAcc): RouteAnalyticsSummary {
  return {
    route: acc.route,
    totalRequests: acc.totalRequests,
    successCount: acc.successCount,
    errorCount: acc.errorCount,
    lastActivityAt: acc.lastActivityAt?.toISOString() ?? null,
  };
}

export function mergeRouteSummaryAcc(into: RouteSummaryAcc, from: RouteSummaryAcc): void {
  into.totalRequests += from.totalRequests;
  into.successCount += from.successCount;
  into.errorCount += from.errorCount;
  if (
    from.lastActivityAt &&
    (!into.lastActivityAt || from.lastActivityAt.getTime() > into.lastActivityAt.getTime())
  ) {
    into.lastActivityAt = from.lastActivityAt;
  }
}

export function routeKeyForRecord(
  record: NormalizedLogRecord,
  routeGroupingEnabled: boolean | undefined,
): string {
  const field = getRouteGroupingField(routeGroupingEnabled);
  return record[field] || record.route || "(unknown)";
}

export function getCreatedRangeForHours(
  hours: number,
  now: Date = new Date(),
): { createdFrom: Date; createdTo: Date } {
  return {
    createdFrom: new Date(now.getTime() - hours * 60 * 60 * 1000),
    createdTo: new Date(now.getTime()),
  };
}

export function getDefaultRouteAnalyticsRange(
  now: Date = new Date(),
): { createdFrom: Date; createdTo: Date } {
  const preset = ROUTE_ANALYTICS_TIME_PRESETS.find((p) => p.id === DEFAULT_ROUTE_ANALYTICS_PRESET)!;
  return getCreatedRangeForHours(preset.hours, now);
}

export function detectTimePreset(
  createdFrom: Date | undefined,
  createdTo: Date | undefined,
  now: Date = new Date(),
  toleranceMs = 90_000,
): (typeof ROUTE_ANALYTICS_TIME_PRESETS)[number]["id"] | "custom" {
  if (!createdFrom || !createdTo) return DEFAULT_ROUTE_ANALYTICS_PRESET;
  const durationMs = createdTo.getTime() - createdFrom.getTime();
  const toSkew = Math.abs(createdTo.getTime() - now.getTime());
  if (toSkew > toleranceMs) return "custom";

  for (const preset of ROUTE_ANALYTICS_TIME_PRESETS) {
    const expected = preset.hours * 60 * 60 * 1000;
    if (Math.abs(durationMs - expected) <= toleranceMs) {
      return preset.id;
    }
  }
  return "custom";
}
